import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Minus, Save, ShieldAlert, Trash2, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';

interface User {
    user_id: number;
    username: string;
    display_name?: string; // Add display_name
    room_no?: string;
    email: string;
    role_type: string;
    floor_level: number;
    timezone?: string;
    smile_score: number;
    unito_points: number;
    unito_enabled: boolean;
    ustore_enabled: boolean;
    salon_enabled: boolean;
    is_suspended: boolean;
    email_verified: boolean;
    password?: string; // Decrypted password
    daily_post_limit_override?: number | null;
    daily_post_limit?: number | null;
    daily_post_count?: number;
    daily_post_remaining?: number | null;
}

export function AdminDashboard() {
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingId, setEditingId] = useState<number | null>(null);
    // Temporary state for edits before save
    const [editForm, setEditForm] = useState<Partial<User>>({});
    const [postRemainingInput, setPostRemainingInput] = useState<Record<number, string>>({});
    const [postRemainingSaving, setPostRemainingSaving] = useState<Record<number, boolean>>({});
    const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [suspendTarget, setSuspendTarget] = useState<User | null>(null);
    const [suspendMode, setSuspendMode] = useState<'quick' | 'edit' | null>(null);
    const [suspendLoading, setSuspendLoading] = useState(false);
    const [resetTarget, setResetTarget] = useState<User | null>(null);
    const [resetPassword, setResetPassword] = useState('');
    const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');

    const renderPortal = (node: ReactNode) => {
        if (typeof document === 'undefined') return null;
        return createPortal(node, document.body);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditStart = (user: User) => {
        setEditingId(user.user_id);
        const { password, daily_post_limit, daily_post_count, daily_post_remaining, daily_post_limit_override, ...rest } = user; // Exclude non-edit fields
        setEditForm(rest);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSave = async (userId: number) => {
        try {
            await api.patch(`/admin/users/${userId}`, editForm);
            setEditingId(null);
            fetchUsers(); // Refresh
        } catch (error) {
            console.error('Failed to update user', error);
            alert('Update failed');
        }
    };

    const handleChange = (field: keyof User, value: any) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const adjustScore = (field: 'smile_score' | 'unito_points', delta: number) => {
        const current = editForm[field] as number || 0;
        handleChange(field, current + delta);
    };

    const toggleBool = (field: 'unito_enabled' | 'ustore_enabled' | 'salon_enabled' | 'is_suspended' | 'email_verified') => {
        handleChange(field, !editForm[field]);
    };

    const formatLimitValue = (value: number | null | undefined) => {
        if (value === null || value === undefined) return '∞';
        return value;
    };

    const handleRemainingChange = (userId: number, value: string) => {
        setPostRemainingInput(prev => ({ ...prev, [userId]: value }));
    };

    const handleRemainingSave = async (user: User) => {
        const raw = postRemainingInput[user.user_id];
        if (raw === undefined || raw.trim() === '') return;
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) return;

        const remaining = Math.max(0, Math.floor(parsed));
        setPostRemainingSaving(prev => ({ ...prev, [user.user_id]: true }));
        try {
            await api.patch(`/admin/users/${user.user_id}/post-remaining`, { remaining });
            setPostRemainingInput(prev => ({ ...prev, [user.user_id]: '' }));
            fetchUsers();
        } catch (error) {
            console.error('Failed to update remaining posts', error);
            alert('残投稿の更新に失敗しました');
        } finally {
            setPostRemainingSaving(prev => ({ ...prev, [user.user_id]: false }));
        }
    };

    // Quick toggle for non-editing row
    const handleQuickToggle = async (user: User, field: 'unito_enabled' | 'ustore_enabled' | 'salon_enabled' | 'is_suspended') => {
        try {
            await api.patch(`/admin/users/${user.user_id}`, { [field]: !user[field] });
            fetchUsers();
        } catch (error) {
            console.error('Failed to toggle', error);
        }
    };

    const handleSuspendQuick = (user: User) => {
        if (user.is_suspended) {
            handleQuickToggle(user, 'is_suspended');
            return;
        }
        setSuspendTarget(user);
        setSuspendMode('quick');
    };

    const handleSuspendEditToggle = (user: User) => {
        const nextValue = !editForm.is_suspended;
        if (!nextValue) {
            handleChange('is_suspended', false);
            return;
        }
        setSuspendTarget(user);
        setSuspendMode('edit');
    };

    const handleConfirmSuspend = async () => {
        if (!suspendTarget || !suspendMode) return;
        setSuspendLoading(true);
        try {
            if (suspendMode === 'quick') {
                await api.patch(`/admin/users/${suspendTarget.user_id}`, { is_suspended: true });
                fetchUsers();
            } else if (suspendMode === 'edit') {
                handleChange('is_suspended', true);
            }
            setSuspendTarget(null);
            setSuspendMode(null);
        } catch (error: any) {
            console.error('Failed to suspend user', error);
            alert(error?.response?.data?.detail || '一時停止に失敗しました');
        } finally {
            setSuspendLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await api.delete(`/admin/users/${deleteTarget.user_id}`);
            setDeleteTarget(null);
            fetchUsers();
        } catch (error: any) {
            console.error('Failed to delete user', error);
            alert(error?.response?.data?.detail || '削除に失敗しました');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleOpenReset = (user: User) => {
        setResetTarget(user);
        setResetPassword('');
        setResetPasswordConfirm('');
        setResetError('');
    };

    const handleResetPassword = async () => {
        if (!resetTarget) return;
        const password = resetPassword.trim();
        if (!password) {
            setResetError('新しいパスワードを入力してください。');
            return;
        }
        if (password !== resetPasswordConfirm) {
            setResetError('パスワードが一致しません。');
            return;
        }
        setResetLoading(true);
        try {
            await api.patch(`/admin/users/${resetTarget.user_id}`, { password });
            setResetTarget(null);
            fetchUsers();
        } catch (error: any) {
            console.error('Failed to reset password', error);
            setResetError(error?.response?.data?.detail || 'パスワードの更新に失敗しました。');
        } finally {
            setResetLoading(false);
        }
    };

    if (isLoading) return <div className="p-8 text-center text-[#d4af37]">Loading Admin Control Panel...</div>;

    return (
        <div className="p-8 min-h-screen bg-[#0c0a09] text-white font-sans">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-serif text-[#d4af37]">Admin Control Panel</h1>
                    <p className="text-gray-500 text-sm italic">"Absolute Power"</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button asChild variant="outline" className="border-[#d4af37] text-[#d4af37]">
                        <Link to="/admin/logs">Logs</Link>
                    </Button>
                    <Button onClick={fetchUsers} variant="outline" className="border-[#d4af37] text-[#d4af37]">Refresh</Button>
                </div>
            </div>

            <div className="overflow-x-auto border border-[#d4af37]/30 rounded-lg">
                <table className="w-full text-xs text-left">
                    <thead className="text-[11px] uppercase bg-[#1c1917] text-[#d4af37] border-b border-[#d4af37]/30">
                        <tr>
                            <th className="px-2 py-2">ID</th>
                            <th className="px-2 py-2">Display Name</th> {/* Add Display Name Column */}
                            <th className="px-2 py-2">RoomNo.</th>
                            <th className="px-2 py-2">User / Email</th>
                            <th className="px-2 py-2">Password</th>
                            <th className="px-2 py-2">Role / Floor</th>
                            <th className="px-2 py-2 text-center">残投稿</th>
                            <th className="px-2 py-2 text-center">Toku Score</th>
                            <th className="px-2 py-2 text-center">Unito Score</th>
                            <th className="px-2 py-2 text-center">Permissions</th>
                            <th className="px-2 py-2 text-center">Status</th>
                            <th className="px-2 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const isEditing = editingId === user.user_id;
                            const dailyLimitDisplay = formatLimitValue(user.daily_post_limit);
                            const dailyRemainingDisplay = formatLimitValue(user.daily_post_remaining);
                            const dailyCount = user.daily_post_count ?? 0;
                            const hasOverride = user.daily_post_limit_override !== null && user.daily_post_limit_override !== undefined;

                            return (
                                <tr key={user.user_id} className="border-b border-[#d4af37]/10 hover:bg-[#d4af37]/5 transition-colors">
                                    <td className="px-2 py-2 font-mono text-gray-400">#{user.user_id}</td>

                                    {/* Display Name */}
                                    <td className="px-2 py-2">
                                        {isEditing ? (
                                            <Input
                                                value={editForm.display_name || ''}
                                                onChange={e => handleChange('display_name', e.target.value)}
                                                className="h-8 bg-black border-[#d4af37]/50"
                                                placeholder="Display Name"
                                            />
                                        ) : (
                                            <div className="font-medium">{user.display_name || '-'}</div>
                                        )}
                                    </td>

                                    {/* RoomNo */}
                                    <td className="px-2 py-2 font-mono text-xs text-gray-300">
                                        {user.room_no || '-'}
                                    </td>

                                    {/* User / Email */}
                                    <td className="px-2 py-2">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <Input
                                                    value={editForm.username}
                                                    onChange={e => handleChange('username', e.target.value)}
                                                    className="h-8 bg-black border-[#d4af37]/50"
                                                />
                                                <Input
                                                    value={editForm.email}
                                                    onChange={e => handleChange('email', e.target.value)}
                                                    className="h-8 bg-black border-[#d4af37]/50"
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="font-bold text-[#fcd34d]">{user.username}</div>
                                                <div className="text-gray-500 text-xs">{user.email}</div>
                                            </div>
                                        )}
                                    </td>

                                    {/* Password */}
                                    <td className="px-2 py-2 font-mono text-xs text-red-300">
                                        {isEditing ? <span className="text-gray-600">—</span> : user.password}
                                    </td>

                                    {/* Role / Floor */}
                                    <td className="px-2 py-2">
                                        {isEditing ? (
                                            <div className="space-y-1">
                                                <select
                                                    value={editForm.role_type}
                                                    onChange={e => handleChange('role_type', e.target.value)}
                                                    className="w-full bg-black border border-[#d4af37]/50 rounded px-2 py-1 text-xs"
                                                >
                                                    <option value="User">User</option>
                                                    <option value="Famiglia">Famiglia</option>
                                                    <option value="Manager">Manager</option>
                                                    <option value="Maestro">Maestro</option>
                                                    <option value="Admin">Admin</option>
                                                </select>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-500">Floor:</span>
                                                    <input
                                                        type="number"
                                                        value={editForm.floor_level}
                                                        onChange={e => handleChange('floor_level', parseInt(e.target.value))}
                                                        className="w-12 bg-black border border-[#d4af37]/50 rounded px-1 py-0.5 text-xs"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-1">
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold 
                                                    ${user.role_type === 'Admin' ? 'bg-purple-900 text-purple-200' : 'bg-gray-800 text-gray-300'}`}>
                                                    {user.role_type}
                                                </span>
                                                <div className="text-xs text-gray-500">Floor {user.floor_level}</div>
                                            </div>
                                        )}
                                    </td>

                                    {/* Remaining Posts */}
                                    <td className="px-2 py-2 text-center">
                                        <div className="text-xs text-gray-300">
                                            残: <span className="font-mono text-[#fcd34d]">{dailyRemainingDisplay}</span>
                                        </div>
                                        <div className="text-[10px] text-gray-500">
                                            今日 {dailyCount}/{dailyLimitDisplay}
                                            {hasOverride && <span className="ml-1 text-[#d4af37]">Override</span>}
                                        </div>
                                        <div className="mt-2 flex items-center justify-center gap-2">
                                            <Input
                                                type="number"
                                                min={0}
                                                value={postRemainingInput[user.user_id] ?? ''}
                                                onChange={(e) => handleRemainingChange(user.user_id, e.target.value)}
                                                className="h-8 w-16 bg-black border-[#d4af37]/50 text-center"
                                                placeholder="変更"
                                            />
                                            <Button
                                                size="sm"
                                                onClick={() => handleRemainingSave(user)}
                                                disabled={postRemainingSaving[user.user_id]}
                                                className="h-8 bg-[#d4af37] text-black hover:bg-[#fcd34d] px-2 text-xs"
                                            >
                                                更新
                                            </Button>
                                        </div>
                                    </td>

                                    {/* Toku Score */}
                                    <td className="px-2 py-2 text-center">
                                        {isEditing ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <button onClick={() => adjustScore('smile_score', -10)} className="text-red-500 hover:text-red-400"><Minus size={14} /></button>
                                                <span className="font-mono w-8 text-center">{editForm.smile_score}</span>
                                                <button onClick={() => adjustScore('smile_score', 10)} className="text-green-500 hover:text-green-400"><Plus size={14} /></button>
                                            </div>
                                        ) : (
                                            <span className="font-mono text-[#d4af37] font-bold">{user.smile_score}</span>
                                        )}
                                    </td>

                                    {/* Unito Score */}
                                    <td className="px-2 py-2 text-center">
                                        {isEditing ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <button onClick={() => adjustScore('unito_points', -1)} className="text-red-500 hover:text-red-400"><Minus size={14} /></button>
                                                <span className="font-mono w-10 text-center">{editForm.unito_points}</span>
                                                <button onClick={() => adjustScore('unito_points', 1)} className="text-green-500 hover:text-green-400"><Plus size={14} /></button>
                                            </div>
                                        ) : (
                                            <span className={`font-mono font-bold ${user.unito_enabled ? 'text-blue-400' : 'text-gray-600'}`}>
                                                {user.unito_points}
                                            </span>
                                        )}
                                    </td>

                                    {/* Permissions */}
                                    <td className="px-2 py-2">
                                        <div className="flex flex-col gap-2 items-center">
                                            {/* Unito Toggle */}
                                            {isEditing ? (
                                                <label className="flex items-center text-xs gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={editForm.unito_enabled} onChange={() => toggleBool('unito_enabled')} />
                                                    Unito
                                                </label>
                                            ) : (
                                                <button
                                                    onClick={() => handleQuickToggle(user, 'unito_enabled')}
                                                    className={`px-2 py-1 text-xs rounded border ${user.unito_enabled ? 'bg-blue-900/30 border-blue-500 text-blue-300' : 'bg-transparent border-gray-700 text-gray-600'}`}
                                                >
                                                    Unito {user.unito_enabled ? 'ON' : 'OFF'}
                                                </button>
                                            )}

                                            {/* U-Store Toggle */}
                                            {isEditing ? (
                                                <label className="flex items-center text-xs gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={editForm.ustore_enabled} onChange={() => toggleBool('ustore_enabled')} />
                                                    U-Store
                                                </label>
                                            ) : (
                                                <button
                                                    onClick={() => handleQuickToggle(user, 'ustore_enabled')}
                                                    className={`px-2 py-1 text-xs rounded border ${user.ustore_enabled ? 'bg-amber-900/30 border-amber-500 text-amber-300' : 'bg-transparent border-gray-700 text-gray-600'}`}
                                                >
                                                    U-Store {user.ustore_enabled ? 'ON' : 'OFF'}
                                                </button>
                                            )}

                                            {/* Salon Toggle */}
                                            {isEditing ? (
                                                <label className="flex items-center text-xs gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={editForm.salon_enabled} onChange={() => toggleBool('salon_enabled')} />
                                                    Salon
                                                </label>
                                            ) : (
                                                <button
                                                    onClick={() => handleQuickToggle(user, 'salon_enabled')}
                                                    className={`px-2 py-1 text-xs rounded border ${user.salon_enabled ? 'bg-pink-900/30 border-pink-500 text-pink-300' : 'bg-transparent border-gray-700 text-gray-600'}`}
                                                >
                                                    Salon {user.salon_enabled ? 'ON' : 'OFF'}
                                                </button>
                                            )}
                                        </div>
                                    </td>

                                    {/* Status */}
                                    <td className="px-2 py-2 text-center">
                                        {isEditing ? (
                                            <label className="flex items-center justify-center text-xs gap-2 cursor-pointer text-red-400 font-bold">
                                                <input
                                                    type="checkbox"
                                                    checked={editForm.is_suspended}
                                                    onChange={() => handleSuspendEditToggle(user)}
                                                />
                                                Suspended
                                            </label>
                                        ) : (
                                            <button
                                                onClick={() => handleSuspendQuick(user)}
                                                className={`flex items-center justify-center gap-1 px-2 py-1 text-xs rounded border ${user.is_suspended
                                                    ? 'bg-red-950 border-red-500 text-red-500 font-bold animate-pulse'
                                                    : 'bg-green-900/20 border-green-800 text-green-700'
                                                    }`}
                                            >
                                                {user.is_suspended ? <><ShieldAlert size={12} /> SUSPENDED</> : 'Active'}
                                            </button>
                                        )}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-2 py-2">
                                        {isEditing ? (
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => handleSave(user.user_id)} className="bg-green-600 hover:bg-green-700 h-8"><Save size={14} /></Button>
                                                <Button size="sm" variant="outline" onClick={handleCancel} className="h-8 border-gray-600"><Minus size={14} /></Button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => handleEditStart(user)} variant="outline" className="h-8 border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10">
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleOpenReset(user)}
                                                    variant="outline"
                                                    className="h-8 border-blue-500/70 text-blue-300 hover:bg-blue-500/10"
                                                >
                                                    <KeyRound size={14} className="mr-1" />
                                                    Reset
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setDeleteTarget(user)}
                                                    variant="outline"
                                                    className="h-8 border-red-500/70 text-red-400 hover:bg-red-500/10"
                                                >
                                                    <Trash2 size={14} className="mr-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        )}
                                    </td>

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Create User Modal - Simple Inline Implementation for speed */}
            <div className="mt-8 p-6 border border-[#d4af37]/30 rounded-lg bg-[#1c1917]">
                <h3 className="text-xl font-bold text-[#d4af37] mb-4 flex items-center gap-2"><Plus size={20} /> Create New User</h3>
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const newUser = {
                        username: fd.get('username'),
                        display_name: fd.get('display_name'), // Add display_name
                        email: fd.get('email'),
                        password: fd.get('password'),
                        role_type: fd.get('role_type'),
                        floor_level: Number(fd.get('floor_level')),
                        smile_score: 100
                    };

                    try {
                        await api.post('/admin/users', newUser);
                        alert('User Created');
                        fetchUsers();
                        (e.target as HTMLFormElement).reset();
                    } catch (err: any) {
                        alert(err.response?.data?.detail || 'Failed to create user');
                    }
                }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">

                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Username</label>
                        <Input name="username" required className="bg-black border-[#d4af37]/30" placeholder="User Name" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Display Name</label>
                        <Input name="display_name" className="bg-black border-[#d4af37]/30" placeholder="Display Name (Opt)" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Email</label>
                        <Input name="email" type="email" required className="bg-black border-[#d4af37]/30" placeholder="user@example.com" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Password</label>
                        <Input name="password" required className="bg-black border-[#d4af37]/30" placeholder="Password" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Role</label>
                        <select name="role_type" className="w-full h-10 bg-black border border-[#d4af37]/30 rounded px-3 text-sm">
                            <option value="User">User</option>
                            <option value="Famiglia">Famiglia</option>
                            <option value="Manager">Manager</option>
                            <option value="Maestro">Maestro</option>
                            <option value="Admin">Admin</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-500">Floor</label>
                        <Input name="floor_level" type="number" defaultValue={3} className="bg-black border-[#d4af37]/30" />
                    </div>

                    <Button type="submit" className="bg-[#d4af37] text-black hover:bg-[#fcd34d] font-bold">
                        Create User
                    </Button>
                </form>
            </div>

            {deleteTarget && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-[#0c0a09] rounded-lg shadow-xl max-w-md w-full border border-red-500/60 p-6 space-y-4 text-white">
                        <h3 className="text-lg font-bold text-red-300">このユーザーを完全に削除しますか？</h3>
                        <div className="text-sm text-gray-300 space-y-1">
                            <div>ユーザー名: <span className="text-[#fcd34d]">{deleteTarget.username}</span></div>
                            <div>表示名: <span className="text-[#fcd34d]">{deleteTarget.display_name || '-'}</span></div>
                            <div>RoomNo: <span className="text-[#fcd34d]">{deleteTarget.room_no || '-'}</span></div>
                            <div>Email: <span className="text-[#fcd34d]">{deleteTarget.email}</span></div>
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            投稿・コメント・いいね・アンケート・チャット・問い合わせ・スコア履歴など、関連するデータが削除されます。
                            この操作は元に戻せません。
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={handleDeleteUser}
                                disabled={deleteLoading}
                                className="bg-red-600 hover:bg-red-700 text-white px-6"
                            >
                                {deleteLoading ? '削除中...' : '削除する'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setDeleteTarget(null)}
                                className="px-6 border-gray-600 text-gray-200"
                                disabled={deleteLoading}
                            >
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {suspendTarget && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-[#0c0a09] rounded-lg shadow-xl max-w-md w-full border border-yellow-500/60 p-6 space-y-4 text-white">
                        <h3 className="text-lg font-bold text-yellow-200">このユーザーを一時停止しますか？</h3>
                        <div className="text-sm text-gray-300 space-y-1">
                            <div>ユーザー名: <span className="text-[#fcd34d]">{suspendTarget.username}</span></div>
                            <div>表示名: <span className="text-[#fcd34d]">{suspendTarget.display_name || '-'}</span></div>
                            <div>RoomNo: <span className="text-[#fcd34d]">{suspendTarget.room_no || '-'}</span></div>
                            <div>Email: <span className="text-[#fcd34d]">{suspendTarget.email}</span></div>
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            一時停止中はログインや投稿が制限されます。再開はいつでも可能です。
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={handleConfirmSuspend}
                                disabled={suspendLoading}
                                className="bg-yellow-500 hover:bg-yellow-600 text-black px-6"
                            >
                                {suspendLoading ? '処理中...' : '一時停止する'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => { setSuspendTarget(null); setSuspendMode(null); }}
                                className="px-6 border-gray-600 text-gray-200"
                                disabled={suspendLoading}
                            >
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {resetTarget && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-[#0c0a09] rounded-lg shadow-xl max-w-md w-full border border-blue-500/60 p-6 space-y-4 text-white">
                        <h3 className="text-lg font-bold text-blue-200">パスワードをリセットします</h3>
                        <div className="text-sm text-gray-300 space-y-1">
                            <div>ユーザー名: <span className="text-[#fcd34d]">{resetTarget.username}</span></div>
                            <div>表示名: <span className="text-[#fcd34d]">{resetTarget.display_name || '-'}</span></div>
                            <div>RoomNo: <span className="text-[#fcd34d]">{resetTarget.room_no || '-'}</span></div>
                        </div>
                        <div className="space-y-2">
                            <Input
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                className="bg-black border-blue-500/40"
                                placeholder="新しいパスワード"
                            />
                            <Input
                                type="password"
                                value={resetPasswordConfirm}
                                onChange={(e) => setResetPasswordConfirm(e.target.value)}
                                className="bg-black border-blue-500/40"
                                placeholder="確認用パスワード"
                            />
                            {resetError && <div className="text-xs text-red-400">{resetError}</div>}
                        </div>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={handleResetPassword}
                                disabled={resetLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                            >
                                {resetLoading ? '更新中...' : '更新する'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setResetTarget(null)}
                                className="px-6 border-gray-600 text-gray-200"
                                disabled={resetLoading}
                            >
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
